import { createReducer, on } from '@ngrx/store';
import { Facets } from '@wam/shared';
import { keys } from 'lodash-es';

import * as assessmentsActions from './assessments.actions';
import { Params } from '@angular/router';
import { hydrateSelectedFacets } from '@app/shared/facets-transformers';
import { AssessmentsState, AssignmentsSaveStatus } from './assessments.state';

function extractFacets(queryParams: Params): Facets {
  const facets = {};
  Object.keys(queryParams).forEach((key) => {
    const value = queryParams[key];
    facets[key] = value.split(',').map((facetValue) => ({
      value: facetValue,
      display: facetValue,
    }));
  });
  return facets ?? {};
}

const initialState: AssessmentsState = {
  level: null,
  hits: {},
  totalHits: 0,
  facets: {},
  selectedFacets: {},
  page: 1,
  classHasStudents: {},
  loadingStudentAssignments: {},
  loadingClassAssignments: {},
  loadingSchoolAssignments: {},
  assignmentsLoadFailed: {},
  studentAssignments: {},
  classAssignments: {},
  schoolAssignments: {},
  assignmentsSaveStatus: {},
  archiveBenchmarksInProgress: {},
};

const withSaveStatus = (
  state: AssessmentsState,
  key: string,
  status: AssignmentsSaveStatus,
): AssessmentsState => ({
  ...state,
  assignmentsSaveStatus: { ...state.assignmentsSaveStatus, [key]: status },
});

const withLoadFailed = (
  state: AssessmentsState,
  key: string,
  failed: boolean,
): AssessmentsState => ({
  ...state,
  assignmentsLoadFailed: { ...state.assignmentsLoadFailed, [key]: failed },
});

const withArchiveInProgress = (
  state: AssessmentsState,
  key: string,
  inProgress: boolean,
): AssessmentsState => ({
  ...state,
  archiveBenchmarksInProgress: { ...state.archiveBenchmarksInProgress, [key]: inProgress },
});

const reducer = createReducer(
  initialState,
  on(assessmentsActions.load, (state) => ({
    ...state,
    page: 1,
  })),
  on(assessmentsActions.loadSuccess, (state, { hits, totalHits }) => ({
    ...state,
    hits: {
      ...state.hits,
      [state.level]: hits,
    },
    totalHits,
  })),
  on(assessmentsActions.continueLoadSuccess, (state, { hits, totalHits }) => ({
    ...state,
    totalHits,
    // @ts-ignore
    hits: {
      ...state.hits,
      [state.level]: [...state.hits[state.level], ...hits],
    },
  })),
  on(assessmentsActions.updateSelectedFacets, (state, { selectedFacets }) => ({
    ...state,
    page: 1,
    classes: [],
    selectedFacets,
  })),
  on(assessmentsActions.updateFacets, (state, { facets }) => ({
    ...state,
    facets,
    selectedFacets: hydrateSelectedFacets(facets, state.selectedFacets),
  })),
  on(assessmentsActions.continueLoad, (state) => ({
    ...state,
    page: state.page + 1,
  })),
  on(assessmentsActions.updateLevel, (state, { level }) => {
    const newHits = {
      ...state.hits,
      [state.level]: state.hits[state.level],
    };
    if (keys(state.selectedFacets).length > 0) {
      delete newHits[state.level];
    }
    return {
      ...state,
      level,
      hits: newHits,
      facets: {},
      selectedFacets: {},
      page: 1,
    };
  }),
  on(assessmentsActions.updateStoreFromRoute, (state, { view, queryParams }) => {
    const selectedFacets = extractFacets(queryParams);
    return {
      ...state,
      level: view,
      selectedFacets,
    };
  }),
  on(assessmentsActions.loadStudentAssignments, (state, { userId }) => ({
    ...withLoadFailed(state, userId, false),
    loadingStudentAssignments: { ...state.loadingStudentAssignments, [userId]: true },
  })),
  on(assessmentsActions.loadStudentAssignmentsFailure, (state, { userId }) => ({
    ...withLoadFailed(state, userId, true),
    loadingStudentAssignments: { ...state.loadingStudentAssignments, [userId]: false },
  })),
  on(assessmentsActions.loadClassAssignments, (state, { classId }) => ({
    ...withLoadFailed(state, classId, false),
    loadingClassAssignments: { ...state.loadingClassAssignments, [classId]: true },
  })),
  on(assessmentsActions.loadClassAssignmentsFailure, (state, { classId }) => ({
    ...withLoadFailed(state, classId, true),
    loadingClassAssignments: { ...state.loadingClassAssignments, [classId]: false },
  })),
  on(assessmentsActions.loadSchoolAssignments, (state, { schoolId, grade }) => ({
    ...withLoadFailed(state, `${schoolId}_${grade}`, false),
    loadingSchoolAssignments: { ...state.loadingSchoolAssignments, [`${schoolId}_${grade}`]: true },
  })),
  on(assessmentsActions.loadSchoolAssignmentsFailure, (state, { schoolId, grade }) => ({
    ...withLoadFailed(state, `${schoolId}_${grade}`, true),
    loadingSchoolAssignments: {
      ...state.loadingSchoolAssignments,
      [`${schoolId}_${grade}`]: false,
    },
  })),
  on(
    assessmentsActions.loadStudentAssignmentsSuccess,
    assessmentsActions.createStudentAssignmentsSuccess,
    (state, { userId, assignments }) => ({
      ...state,
      loadingStudentAssignments: { ...state.loadingStudentAssignments, [userId]: false },
      studentAssignments: {
        ...state.studentAssignments,
        [userId]: [
          ...(state.studentAssignments[userId] ?? []).filter(
            ({ documentId }) =>
              !assignments.find((assignment) => assignment.documentId === documentId),
          ),
          ...assignments,
        ],
      },
    }),
  ),
  on(
    assessmentsActions.loadClassAssignmentsSuccess,
    assessmentsActions.createClassAssignmentsSuccess,
    (state, { classId, assignments }) => ({
      ...state,
      loadingClassAssignments: { ...state.loadingClassAssignments, [classId]: false },
      classAssignments: {
        ...state.classAssignments,
        [classId]: [
          ...(state.classAssignments[classId] ?? []).filter(
            ({ documentId }) =>
              !assignments.find((assignment) => assignment.documentId === documentId),
          ),
          ...assignments,
        ],
      },
    }),
  ),
  on(
    assessmentsActions.loadSchoolAssignmentsSuccess,
    assessmentsActions.createSchoolAssignmentsSuccess,
    (state, { schoolId, grade, assignments }) => ({
      ...state,
      loadingSchoolAssignments: {
        ...state.loadingSchoolAssignments,
        [`${schoolId}_${grade}`]: false,
      },
      schoolAssignments: {
        ...state.schoolAssignments,
        [`${schoolId}_${grade}`]: [
          ...(state.schoolAssignments[`${schoolId}_${grade}`] ?? []).filter(
            ({ documentId }) =>
              !assignments.find((assignment) => assignment.documentId === documentId),
          ),
          ...assignments,
        ],
      },
    }),
  ),
  on(
    assessmentsActions.createStudentAssignments,
    assessmentsActions.createClassAssignments,
    assessmentsActions.createSchoolAssignments,
    (state, { key }) => withSaveStatus(state, key, 'inProgress'),
  ),
  on(
    assessmentsActions.createStudentAssignmentsSuccess,
    assessmentsActions.createClassAssignmentsSuccess,
    assessmentsActions.createSchoolAssignmentsSuccess,
    (state, { key }) => withSaveStatus(state, key, 'succeeded'),
  ),
  on(assessmentsActions.createAssignmentsFailure, (state, { key }) =>
    withSaveStatus(state, key, 'failed'),
  ),
  on(
    assessmentsActions.archiveStudentBenchmarks,
    assessmentsActions.archiveClassBenchmarks,
    assessmentsActions.archiveSchoolBenchmarks,
    (state, { key }) => withArchiveInProgress(state, key, true),
  ),
  on(
    assessmentsActions.archiveStudentBenchmarksSuccess,
    assessmentsActions.archiveStudentBenchmarksFailure,
    assessmentsActions.archiveClassBenchmarksSuccess,
    assessmentsActions.archiveClassBenchmarksFailure,
    assessmentsActions.archiveSchoolBenchmarksSuccess,
    assessmentsActions.archiveSchoolBenchmarksFailure,
    (state, { key }) => withArchiveInProgress(state, key, false),
  ),
);

export function assessmentsReducer(state, action) {
  return reducer(state, action);
}
